import { flash, getSymbol, Modal, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { Merchandise, MerchandiseCatalogItem, MerchandiseCatalogItemStatus, OversellPolicy, Product } from "types";
import { getInitialValues, getPayload, validationSchema, VariantFormValues } from "./editVariantHelper";
import { ProductInfoHeader } from "./ProductInfoHeader";
import { ToggleVariantStatusModal } from "./ToggleVariantStatusModal";
import { api } from "@/api/api";
import { NumberInlineValuePopover } from "@/components/Common/NumberInlineValuePopover";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { CurrencyInput } from "@/components/Input/CurrencyInput";
import { ProductPhotoList } from "@/components/Product/ProductPhotoList";
import { useResource } from "@/hooks/useResource";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { isInventoryTracked, MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { useLocalizationStore } from "@/stores/localizationStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface EditVariantModalProps {
    isOpen: boolean;
    onClose: () => void;
    catalogItemId: string;
    merchandise: Merchandise;
    headerTitle?: string;
    variantPath?: string;
    onSavedCatalogItem?: (catalogItem: MerchandiseCatalogItem) => void;
    publicVariantCount: number;
}

export const EditVariantModal = ({
    isOpen,
    onClose,
    catalogItemId,
    merchandise,
    headerTitle,
    variantPath,
    onSavedCatalogItem,
    publicVariantCount,
}: EditVariantModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent
                catalogItemId={catalogItemId}
                merchandise={merchandise}
                headerTitle={headerTitle}
                variantPath={variantPath}
                publicVariantCount={publicVariantCount}
                onClose={onClose}
                onSavedCatalogItem={onSavedCatalogItem}
            />
        </Modal>
    );
};

const ModalContent = ({
    catalogItemId,
    onClose,
    merchandise,
    headerTitle,
    variantPath,
    onSavedCatalogItem,
    publicVariantCount,
}: Except<EditVariantModalProps, "isOpen">) => {
    const seller = getSeller();
    const locale = useLocalizationStore().getLocale();
    const [isStrikeThroughPricePopoverOpen, { toggle: toggleStrikeThroughPricePopover }] = useVisibility(false);
    const unpublishLastVariantModal = useModal(false);
    const pendingStatusRef = useRef<MerchandiseCatalogItemStatus | null>(null);
    const currency = getSymbol(seller.currency, locale);

    const { data: catalogItem, isLoading } = useResource<MerchandiseCatalogItem>(["catalogItems", catalogItemId]);

    const { mutateAsync: saveCatalogItem } = useMutation(async (payload: Partial<MerchandiseCatalogItem>) => {
        const { data } = await api.put<MerchandiseCatalogItem>(`/catalogItems/${catalogItemId}`, payload);
        return data;
    });

    if (isLoading || !catalogItem) return null;

    const initialValues: VariantFormValues = getInitialValues(catalogItem, isInventoryTracked(merchandise));

    const handleSubmit = async (values: VariantFormValues) => {
        const payload = getPayload(values, merchandise.id, initialValues.status);
        const savedCatalogItem = await saveCatalogItem(payload);
        onSavedCatalogItem?.(savedCatalogItem);
        updateCache("catalogItems", savedCatalogItem as MerchandiseCatalogItem & { id: ID });

        const [{ data: product }, { data: ware }] = await Promise.all([
            api.get<Product>(`/products/${merchandise.id}`),
            api.get<Merchandise>(`/${MERCHANDISE_API_PATH}/${merchandise.id}`),
        ]);
        updateCache("products", product);
        updateCache(MERCHANDISE_CACHE_KEY, ware);

        flash.success("Variant saved");
        onClose();
    };

    return (
        <XolaFormik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ values, setFieldValue, handleSubmit, isSubmitting, errors }) => {
                const handleUnpublishLastVariantModalClose = () => {
                    pendingStatusRef.current = null;
                    unpublishLastVariantModal.hide();
                };

                const handleUnpublishLastVariantConfirm = () => {
                    const pendingStatus = pendingStatusRef.current;
                    if (pendingStatus) {
                        setFieldValue("status", pendingStatus);
                    }
                    pendingStatusRef.current = null;
                    unpublishLastVariantModal.hide();
                };

                const handleStatusChange = (checked: boolean) => {
                    const nextStatus = checked
                        ? MerchandiseCatalogItemStatus.PUBLISHED
                        : MerchandiseCatalogItemStatus.DRAFT;

                    const isUnpublishingLastVariant =
                        !checked &&
                        values.status === MerchandiseCatalogItemStatus.PUBLISHED &&
                        publicVariantCount === 1;

                    if (isUnpublishingLastVariant) {
                        pendingStatusRef.current = nextStatus;
                        unpublishLastVariantModal.show();
                        return;
                    }

                    setFieldValue("status", nextStatus);
                };

                return (
                    <>
                        <Modal.Header onClose={onClose}>{headerTitle ?? "Edit Item"}</Modal.Header>
                        <Modal.Body>
                            <div className="space-y-3">
                                <ProductInfoHeader
                                    variantName={merchandise.name}
                                    variantPath={variantPath}
                                    merchandise={merchandise}
                                    hasVariants={false}
                                    sku={catalogItem?.sku}
                                />

                                <Switch.Group>
                                    <Switch
                                        data-testid="publish-product-switch"
                                        isChecked={values.status === MerchandiseCatalogItemStatus.PUBLISHED}
                                        onChange={handleStatusChange}
                                    />
                                    <div className="ml-3 flex items-center text-sm font-bold">
                                        <Switch.Label className="ml-0">Publish this item</Switch.Label>
                                    </div>
                                </Switch.Group>

                                <FormField name="sku" label="SKU">
                                    <InputField trim />
                                </FormField>

                                <FormField name="barcode" label="Barcode">
                                    <InputField trim />
                                </FormField>

                                <FormField name="price" label="Price (Cost to Customer)">
                                    <CurrencyInput
                                        value={values.price}
                                        onValueChange={({ floatValue }: { floatValue?: number }) =>
                                            setFieldValue("price", floatValue)
                                        }
                                    />
                                </FormField>

                                <Switch.Group>
                                    <Switch
                                        data-testid="show-compare-price-switch"
                                        isChecked={values.hasStrikeThroughPrice}
                                        onChange={(checked: boolean) => setFieldValue("hasStrikeThroughPrice", checked)}
                                    />
                                    <Switch.Label className="ml-3">
                                        Show compare-at price
                                        {values.hasStrikeThroughPrice && (
                                            <>
                                                &nbsp;
                                                <span onClick={(e) => e.stopPropagation()}>
                                                    <NumberInlineValuePopover
                                                        isOpen={isStrikeThroughPricePopoverOpen}
                                                        value={values.strikeThroughPrice}
                                                        text={`${currency}${values.strikeThroughPrice}`}
                                                        error={errors?.strikeThroughPrice}
                                                        min={0}
                                                        onOpenChange={toggleStrikeThroughPricePopover}
                                                        onChange={(value: number) =>
                                                            setFieldValue("strikeThroughPrice", value)
                                                        }
                                                    />
                                                </span>
                                            </>
                                        )}
                                    </Switch.Label>
                                </Switch.Group>

                                <InventorySettingsSection values={values} setFieldValue={setFieldValue} />

                                {catalogItem && <ProductPhotoList catalogItem={catalogItem} />}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton type="submit" isLoading={isSubmitting} onClick={handleSubmit}>
                                Done
                            </SubmitButton>
                        </Modal.Footer>

                        <ToggleVariantStatusModal
                            isOpen={unpublishLastVariantModal.isVisible}
                            selectedCount={1}
                            title="Unpublish this variant?"
                            description="This will make this variant hidden on the make a purchase screen and from checkout."
                            onClose={handleUnpublishLastVariantModalClose}
                            onApply={handleUnpublishLastVariantConfirm}
                        />
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface InventorySettingsSectionProps {
    values: VariantFormValues;
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
}

const InventorySettingsSection = ({ values, setFieldValue }: InventorySettingsSectionProps) => {
    const [isLowInventoryPopoverOpen, { toggle: toggleLowInventoryPopover }] = useVisibility(false);

    if (!values.trackQuantity) {
        return null;
    }

    return (
        <>
            <Switch.Group>
                <Switch
                    isChecked={values.notifyLowInventory}
                    onChange={(checked: boolean) => setFieldValue("notifyLowInventory", checked)}
                />
                <Switch.Label className="ml-3">
                    Notify me if inventory falls below
                    {values.notifyLowInventory && (
                        <>
                            &nbsp;
                            <span onClick={(e) => e.stopPropagation()}>
                                <NumberInlineValuePopover
                                    isOpen={isLowInventoryPopoverOpen}
                                    value={values.lowInventoryThreshold}
                                    text={values.lowInventoryThreshold.toString()}
                                    min={0}
                                    onOpenChange={toggleLowInventoryPopover}
                                    onChange={(value: number) => setFieldValue("lowInventoryThreshold", value)}
                                />
                            </span>
                        </>
                    )}
                </Switch.Label>
            </Switch.Group>

            <Switch.Group>
                <Switch
                    data-testid="continue-selling-switch"
                    isChecked={values.oversellPolicy === OversellPolicy.ALLOW_NEGATIVE}
                    onChange={(checked: boolean) =>
                        setFieldValue(
                            "oversellPolicy",
                            checked ? OversellPolicy.ALLOW_NEGATIVE : OversellPolicy.REQUIRE_AVAILABILITY,
                        )
                    }
                />
                <Switch.Label className="ml-3">Continue selling when out of stock</Switch.Label>
            </Switch.Group>
        </>
    );
};
